import { fromJS } from 'immutable'
import { attachDriver, Signal } from 'rwwa-rx-state-machine'
import { StateMap } from 'typings/immutable'
import { isHomeUrl } from '@classic/Foundation/Navigation/Nav'
import { AppRoutes } from '@core/AppRoutes'
import type { RouteString, RouteStack } from './types'
import { isBetLoopRoute, isRaceRoute } from './helpers/isRouteCheck'
import { NavigatedTo, ReplaceCurrentUrl } from './signals'

interface RouteState {
  currentUrl: RouteString
  routeStack: RouteStack
  backUrl: RouteString
  lastRacesPath: RouteString
  lastBetLoopPath: RouteString
}

const queryAndHashRegex = /(\?|#).*$/

export const defaultState: RouteState = {
  currentUrl: (location.pathname as RouteString) || '/',
  routeStack: ['/'],
  backUrl: '/',
  lastRacesPath: AppRoutes.Meetings,
  lastBetLoopPath: AppRoutes.BetLoop,
}

export type RouteDriverStateMap = StateMap<RouteState>

export function routeDriver(
  state: RouteDriverStateMap = fromJS(defaultState),
  signal: Signal
): RouteDriverStateMap {
  switch (signal.tag) {
    case NavigatedTo: {
      const payloadUrl = signal.data.url.replace('#', '') as string
      const isHome = isHomeUrl(payloadUrl)
      const newRoute = isHome ? AppRoutes.Home : (payloadUrl as RouteString)

      const currentStack = state.get('routeStack').toJS()
      const lastPath = currentStack[currentStack.length - 1]

      if (newRoute.replace(queryAndHashRegex, '') === lastPath.replace(queryAndHashRegex, '')) {
        return state
      }

      const newStack = getNewStack(newRoute, currentStack)
      const currentUrl = newStack[newStack.length - 1]

      const lastRacesPath = getLastRacesPath({
        currentUrl,
        lastRacesPath: state.get('lastRacesPath'),
      })

      const lastBetLoopPath = getLastBetLoopPath({
        currentUrl,
        lastBetLoopPath: state.get('lastBetLoopPath'),
      })

      return state.merge({
        currentUrl,
        routeStack: newStack,
        backUrl: getBackUrlFromStack(newStack),
        lastRacesPath,
        lastBetLoopPath,
      })
    }

    case ReplaceCurrentUrl: {
      const newUrl = signal.data.url
      const isRacePath = signal.data.isRacePath
      if (!newUrl) return state

      const currentStack = state.get('routeStack').toJS()
      currentStack[currentStack.length - 1] = newUrl
      const currentUrl = currentStack[currentStack.length - 1]

      return state.merge({
        currentUrl,
        routeStack: currentStack,
        lastRacesPath: isRacePath ? currentUrl : state.get('lastRacesPath'),
      })
    }

    default:
      return state
  }
}

export const state$ = attachDriver<RouteDriverStateMap>({
  path: 'routeDriver',
  driver: routeDriver,
})

// =============
// Local Helpers
// =============

function getLastRacesPath({
  lastRacesPath,
  currentUrl,
}: Pick<RouteState, 'lastRacesPath' | 'currentUrl'>): RouteString {
  const cleanedCurrentUrlPath = currentUrl.replace('#', '').split('?')[0]
  const isCurrentUrlRacePath = isRaceRoute(cleanedCurrentUrlPath)
  const isCurrentUrlMeetingsPath =
    isCurrentUrlRacePath && cleanedCurrentUrlPath === AppRoutes.Meetings

  const newLastRacesPath = isCurrentUrlRacePath
    ? isCurrentUrlMeetingsPath
      ? AppRoutes.Meetings
      : currentUrl
    : lastRacesPath

  return newLastRacesPath
}

function getLastBetLoopPath({
  lastBetLoopPath,
  currentUrl,
}: Pick<RouteState, 'lastBetLoopPath' | 'currentUrl'>): RouteString {
  return isBetLoopRoute(currentUrl) ? currentUrl : lastBetLoopPath
}

function getNewStack(newRoute: RouteString, currentStack: RouteStack): RouteStack {
  // Reset stack if home
  if (newRoute === AppRoutes.Home) return [AppRoutes.Home]

  // Remove last route when user navigates back
  if (newRoute === getBackUrlFromStack(currentStack) && currentStack.length > 1) {
    return currentStack.slice(0, -1) as RouteStack
  }

  // Add new route to stack
  return [...currentStack, newRoute]
}

function getBackUrlFromStack(stack: RouteStack): RouteString {
  return stack[stack.length - 2] || AppRoutes.Home
}
